import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { Check, Certificate, ArrowSquareOut } from "@phosphor-icons/react";
import { useExamCategories } from "../../lib/examCategories";

const DEFAULT = {
  title: "",
  description: "",
  duration_minutes: 30,
  passing_marks: 0,
  negative_marking: false,
  shuffle_questions: false,
  shuffle_options: false,
  proctoring_enabled: true,
  price_credits: 0,
  certificate_price_credits: 0,
  question_ids: [],
  status: "draft",
  subject_id: null,
  category: "general_knowledge",
  certificate_template: "classic",
  certificate_body_text: "",
  certificate_signature_name: "",
  certificate_signature_title: "",
  certificate_bg_file_id: "",
  certificate_logo_file_id: "",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ExamForm() {
  const { eid } = useParams();
  const editing = eid && eid !== "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT);
  const [questions, setQuestions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const { categories: examCategories } = useExamCategories();

  useEffect(() => {
    (async () => {
      const [qs, tpls] = await Promise.all([
        api.get("/questions"),
        api.get("/certificates/templates").catch(() => ({ data: [] })),
      ]);
      setQuestions(qs.data);
      setTemplates(tpls.data);
      if (editing) {
        try {
          const r = await api.get(`/exams/${eid}`);
          setForm({ ...DEFAULT, ...r.data });
        } catch { /* fall back to defaults */ }
      }
      setLoading(false);
    })();
  }, [editing, eid]);

  const toggleQ = (qid) => {
    setForm((f) => ({
      ...f,
      question_ids: f.question_ids.includes(qid)
        ? f.question_ids.filter((x) => x !== qid)
        : [...f.question_ids, qid],
    }));
  };

  const submit = async (status) => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.category) { toast.error("Category required"); return; }
    if (form.question_ids.length === 0) { toast.error("Select at least one question"); return; }
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      passing_marks: Number(form.passing_marks),
      price_credits: Number(form.price_credits || 0),
      certificate_price_credits: Number(form.certificate_price_credits || 0),
      status: status || form.status,
    };
    try {
      if (editing) await api.put(`/exams/${eid}`, payload);
      else await api.post("/exams", payload);
      toast.success("Saved");
      navigate("/app/exams-manage");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const previewCert = () => {
    const useCustom = !!(form.certificate_bg_file_id || form.certificate_logo_file_id);
    const params = new URLSearchParams({ template: useCustom ? "custom" : (form.certificate_template || "classic") });
    if (editing) params.set("exam_id", eid);
    if (form.certificate_bg_file_id) params.set("bg_file_id", form.certificate_bg_file_id);
    if (form.certificate_logo_file_id) params.set("logo_file_id", form.certificate_logo_file_id);
    // Attach bearer via ?auth= so window.open works even when the browser strips headers
    const tok = localStorage.getItem("session_token");
    if (tok) params.set("auth", tok);
    const url = `${BACKEND}/api/certificates/preview?${params.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalMarks = questions.filter((q) => form.question_ids.includes(q.question_id)).reduce((s, q) => s + Number(q.marks || 0), 0);

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div>
      <PageHeader overline={editing ? "Edit Exam" : "New Exam"} title={editing ? form.title || "Edit exam" : "Create a new exam"} />

      <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200 mb-8">
        <div className="lg:col-span-2 p-8 border-r border-zinc-200 space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Title</div>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="exam-title-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Description</div>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-zinc-300 p-3 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="exam-desc-input"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">
              Category <span className="text-red-600">*</span>
            </div>
            <select
              value={form.category || ""}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="exam-category-select"
              required
            >
              {examCategories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
            <div className="text-[10px] text-zinc-500 mt-1">Sector this exam belongs to — used for filters &amp; catalog badges. Manage the list from Institution → Categories.</div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Pick questions ({form.question_ids.length} selected)</div>
            <div className="border border-zinc-200 max-h-[420px] overflow-y-auto" data-testid="question-picker">
              {questions.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500">No questions in bank. Create some first.</div>
              ) : questions.map((q, i) => {
                const sel = form.question_ids.includes(q.question_id);
                return (
                  <button
                    key={q.question_id}
                    type="button"
                    onClick={() => toggleQ(q.question_id)}
                    className={`w-full text-left p-4 flex items-start gap-3 ${i < questions.length - 1 ? "border-b border-zinc-200" : ""} ${sel ? "bg-zinc-50" : "hover:bg-zinc-50"}`}
                    data-testid={`pick-q-${q.question_id}`}
                  >
                    <span className={`mt-0.5 w-6 h-6 flex items-center justify-center border ${sel ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300"}`}>
                      {sel && <Check size={12} weight="bold" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-snug">{q.text}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                        {q.type} · {q.difficulty} · {q.marks} marks
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-8 bg-zinc-50 space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">Total Marks</div>
            <div className="font-display text-3xl font-bold tabular">{totalMarks}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Duration (minutes)</div>
            <input
              type="number"
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="exam-duration-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Passing Marks</div>
            <input
              type="number"
              step="0.5"
              value={form.passing_marks}
              onChange={(e) => setForm({ ...form, passing_marks: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="exam-passing-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Price (credits)</div>
            <input
              type="number"
              min="0"
              value={form.price_credits}
              onChange={(e) => setForm({ ...form, price_credits: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="exam-price-credits-input"
            />
            <div className="text-[10px] text-zinc-500 mt-1">0 = free. Debited on exam start.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Certificate download (credits)</div>
            <input
              type="number"
              min="0"
              value={form.certificate_price_credits}
              onChange={(e) => setForm({ ...form, certificate_price_credits: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="exam-cert-price-input"
            />
            <div className="text-[10px] text-zinc-500 mt-1">0 = free. Debited once per certificate.</div>
          </div>
          {[
            ["negative_marking", "Negative marking"],
            ["shuffle_questions", "Shuffle questions"],
            ["shuffle_options", "Shuffle options"],
            ["proctoring_enabled", "AI proctoring (webcam + mic + violations)"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer" data-testid={`exam-${k}`}>
              <input
                type="checkbox"
                checked={!!form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}

          <div className="space-y-2 pt-4 border-t border-zinc-200">
            <button
              onClick={() => submit("published")}
              className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800"
              data-testid="save-publish-btn"
            >Save & Publish</button>
            <button
              onClick={() => submit("draft")}
              className="w-full border border-zinc-300 py-3 text-sm font-medium hover:bg-zinc-50"
              data-testid="save-draft-btn"
            >Save as Draft</button>
          </div>
        </div>
      </div>

      {/* Certificate design section */}
      <div className="border border-zinc-200" data-testid="certificate-design-section">
        <div className="px-8 py-6 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-1 inline-flex items-center gap-2">
              <Certificate size={12} weight="fill" /> Certificate design
            </div>
            <h2 className="font-display text-xl font-bold">Sample certificate</h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              Pick a template, add a signature and custom body text. Students who pass this exam receive a certificate rendered in your chosen style.
            </p>
          </div>
          <button
            onClick={previewCert}
            className="inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 text-sm font-medium hover:bg-zinc-900 hover:text-white"
            data-testid="cert-preview-btn"
          >
            <ArrowSquareOut size={14} /> Preview sample PDF
          </button>
        </div>

        <div className="p-8 grid md:grid-cols-3 gap-4">
          {templates.map((tpl) => {
            const selected = form.certificate_template === tpl.id;
            return (
              <button
                type="button"
                key={tpl.id}
                onClick={() => setForm({ ...form, certificate_template: tpl.id })}
                className={`text-left border-2 transition-all ${selected ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`cert-template-${tpl.id}`}
              >
                <TemplateThumbnail template={tpl} />
                <div className="p-4 border-t border-zinc-200">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-base font-bold">{tpl.name}</div>
                    {selected && (
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-900 text-white px-2 py-0.5">Selected</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{tpl.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-8 pb-8 grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Signature name</div>
            <input
              value={form.certificate_signature_name || ""}
              onChange={(e) => setForm({ ...form, certificate_signature_name: e.target.value })}
              placeholder="e.g. Dr. Jane Smith"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="cert-signature-name-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Signature title</div>
            <input
              value={form.certificate_signature_title || ""}
              onChange={(e) => setForm({ ...form, certificate_signature_title: e.target.value })}
              placeholder="e.g. Dean of Academic Affairs"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="cert-signature-title-input"
            />
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Custom body text</div>
            <input
              value={form.certificate_body_text || ""}
              onChange={(e) => setForm({ ...form, certificate_body_text: e.target.value })}
              placeholder="Default: has successfully completed the assessment"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="cert-body-text-input"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Appears above the exam title on the certificate.</div>
          </div>
          <div className="md:col-span-2 border-t border-zinc-200 pt-4 mt-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Per-exam design override (optional)</div>
            <div className="text-xs text-zinc-500 mb-3">Overrides the institution&rsquo;s default design just for this exam. Leave empty to inherit the institution&rsquo;s certificate design.</div>
            <div className="grid grid-cols-2 gap-3">
              <ExamCertAsset
                label="Background artwork"
                fileId={form.certificate_bg_file_id}
                onChange={(v) => setForm({ ...form, certificate_bg_file_id: v })}
                testId="cert-exam-bg"
                requireLandscape
              />
              <ExamCertAsset
                label="Logo / seal"
                fileId={form.certificate_logo_file_id}
                onChange={(v) => setForm({ ...form, certificate_logo_file_id: v })}
                testId="cert-exam-logo"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateThumbnail({ template }) {
  const primary = template.primary || "#09090b";
  const accent = template.accent || "#52525b";
  const bg = template.background || "#ffffff";
  const isModern = template.id === "modern";
  const isAcademic = template.id === "academic";
  const isElegant = template.id === "elegant";
  return (
    <div className="aspect-[4/3] relative p-3" style={{ background: bg }}>
      {isModern && (
        <div className="absolute top-0 left-0 right-0 h-4" style={{ background: primary }}>
          <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: accent }} />
        </div>
      )}
      <div
        className={`absolute inset-2 ${isModern ? "top-6" : ""} border`}
        style={{ borderColor: primary, borderWidth: isAcademic ? 2 : 1 }}
      >
        {isAcademic && (
          <div className="absolute inset-0.5 border" style={{ borderColor: accent, borderWidth: 0.5 }} />
        )}
        <div className="flex flex-col items-center justify-center h-full text-center gap-1 px-2">
          <div className="text-[9px] tracking-[0.2em] font-bold" style={{ color: isModern ? primary : primary }}>CERTIFICATE</div>
          <div className="text-[7px]" style={{ color: accent }}>OF COMPLETION</div>
          <div className="w-full h-px my-1" style={{ background: accent, opacity: 0.4 }} />
          <div className="text-[8px] italic" style={{ color: accent }}>Alex Sample Student</div>
          <div className="text-[7px] font-bold" style={{ color: primary }}>Sample Assessment</div>
          {isElegant && <div className="text-[7px] mt-1" style={{ color: accent }}>★ ★ ★</div>}
        </div>
      </div>
    </div>
  );
}


function ExamCertAsset({ label, fileId, onChange, testId, requireLandscape = false }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);
  const url = fileId ? `${BACKEND}/api/files/${fileId}?auth=${encodeURIComponent(localStorage.getItem("session_token") || "")}` : "";
  const doUpload = async (file) => {
    if (!file.type.startsWith("image/")) { toast.error("PNG/JPG only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    if (requireLandscape) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      URL.revokeObjectURL(img.src);
      if (img.width && img.height && img.width < img.height) {
        if (!window.confirm("This image looks portrait-oriented. Backgrounds work best in landscape A4. Continue anyway?")) return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.file_id);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div className="border border-zinc-200 p-3" data-testid={`${testId}-uploader`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className="aspect-video bg-zinc-50 border border-dashed border-zinc-300 flex items-center justify-center overflow-hidden">
        {fileId ? (
          <img src={url} alt={label} className="w-full h-full object-contain" data-testid={`${testId}-preview-img`} onError={(e) => { e.target.style.opacity = 0.3; }} />
        ) : (
          <div className="text-[11px] text-zinc-400">No override</div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) doUpload(f); }}
        data-testid={`${testId}-input`}
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="border border-zinc-300 px-2 py-1 text-[11px] hover:border-zinc-900 disabled:opacity-50"
          data-testid={`${testId}-upload-btn`}
        >
          {uploading ? "Uploading…" : fileId ? "Replace" : "Upload"}
        </button>
        {fileId && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="border border-zinc-300 px-2 py-1 text-[11px] text-red-700 hover:border-red-500"
            data-testid={`${testId}-remove-btn`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
